import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';
import { CONFIG } from '../config/constants';
import { UserRole, USER_ROLES, rolePermissions } from '../config/roles';

// Define the shape of user data
interface User {
  id: number;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  phone?: string;
  birth_date?: string;
  bio?: string;
  job_title?: string;
  company?: string;
  location?: string;
  website?: string;
  preferences?: {
    notifications?: boolean;
    email_notifications?: boolean;
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    timezone?: string;
  };
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
}

// Define the shape of the auth context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
  canCreateQuizzes: () => boolean;
  canManageUsers: () => boolean;
  canManageDocuments: () => boolean;
  canViewDocuments: () => boolean;
  canTakeQuizzes: () => boolean;
  canViewHistory: () => boolean;
  canManageQuiz: (quizUserId: number) => boolean;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  updateUser: () => {},
  canCreateQuizzes: () => false,
  canManageUsers: () => false,
  canManageDocuments: () => false,
  canViewDocuments: () => false,
  canTakeQuizzes: () => false,
  canViewHistory: () => false,
  canManageQuiz: () => false,
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if the user is already logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Check if there's a token in localStorage
        const { token } = authService.getCurrentUser();
        
        if (token) {
          // If there's a token, fetch the user data
          try {
            const response = await fetch(`${CONFIG.API.API_URL}/user`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
              },
            });
            
            if (response.ok) {
              const responseData = await response.json();
              // Handle both old format (direct user object) and new format (wrapped in data)
              const userData = responseData.data || responseData;
              // Ensure role exists, default to 'member' if not present
              const userWithRole: User = {
                ...userData,
                role: userData.role || USER_ROLES.MEMBER
              };
              setUser(userWithRole);
            } else {
              // If the token is invalid, clear it
              authService.logout();
              setUser(null);
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            authService.logout();
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Update user function
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  // Role-based permission checks using centralized functions
  const canCreateQuizzes = (): boolean => {
    return rolePermissions.canCreateQuizzes(user?.role);
  };

  const canManageUsers = (): boolean => {
    return rolePermissions.canManageUsers(user?.role);
  };

  const canManageDocuments = (): boolean => {
    return rolePermissions.canManageDocuments(user?.role);
  };

  const canViewDocuments = (): boolean => {
    return rolePermissions.canViewDocuments(user?.role);
  };

  const canTakeQuizzes = (): boolean => {
    return rolePermissions.canTakeQuizzes(user?.role);
  };

  const canViewHistory = (): boolean => {
    return rolePermissions.canViewHistory(user?.role);
  };

  const canManageQuiz = (quizUserId: number): boolean => {
    if (!user) return false;
    // User can manage quiz if they own it OR if they are admin
    return user.id === quizUserId || rolePermissions.canManageUsers(user.role);
  };

  // Provide the auth context to children components
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
        canCreateQuizzes,
        canManageUsers,
        canManageDocuments,
        canViewDocuments,
        canTakeQuizzes,
        canViewHistory,
        canManageQuiz,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 